import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ArrowLeft, Lock, Eye, EyeOff, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useApi } from "../hooks/useApi";
import { apiService } from "../services/apiService";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { execute: executeReset, loading } = useApi();
  const { execute: executeVerify, loading: verifying } = useApi();
  const [tokenValid, setTokenValid] = useState(false);
  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: ""
  });
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link");
      navigate('/forgot-password');
      return;
    }

    verifyToken();
  }, [token, navigate]);

  const verifyToken = async () => {
    try {
      await executeVerify(
        () => apiService.auth.verifyResetToken(token),
        { showSuccessToast: false }
      );
      setTokenValid(true);
    } catch (error) {
      toast.error("Invalid or expired reset link");
      navigate('/forgot-password');
    }
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters long";
    if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter";
    if (!/[a-z]/.test(password)) return "Password must contain at least one lowercase letter";
    if (!/\d/.test(password)) return "Password must contain at least one digit";
    return "";
  };

  const validateConfirmPassword = (confirmPassword) => {
    if (!confirmPassword) return "Please confirm your password";
    if (confirmPassword !== password) return "Passwords do not match";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const passwordError = validatePassword(password);
    const confirmPasswordError = validateConfirmPassword(confirmPassword);
    
    setErrors({
      password: passwordError,
      confirmPassword: confirmPasswordError
    });

    if (passwordError || confirmPasswordError) return;

    // Loading is now handled by useApi hook
    
    try {
      await executeReset(
        () => apiService.auth.resetPassword(token, password),
        { 
          showSuccessToast: true,
          successMessage: "Password reset successfully!"
        }
      );
      navigate('/auth');
    } catch (error) {
      // Error is already handled by useApi hook
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-black/50 backdrop-blur-xl border border-white/20">
          <CardContent className="p-8">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-white mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold text-white mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Verifying Reset Link
              </h2>
              <p className="text-gray-300" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Please wait while we verify your reset link...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-black/50 backdrop-blur-xl border border-white/20">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-4" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Invalid Reset Link
              </h2>
              
              <p className="text-gray-300 mb-6" style={{fontFamily: 'Montserrat, sans-serif'}}>
                This reset link is invalid or has expired. Please request a new password reset.
              </p>
              
              <Link to="/forgot-password">
                <Button className="w-full bg-white text-black hover:bg-gray-100 font-semibold">
                  Request New Reset Link
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-black/50 backdrop-blur-xl border border-white/20">
        <CardHeader className="text-center pb-4">
          <CardTitle className="text-2xl font-bold text-white" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Reset Password
          </CardTitle>
          <p className="text-gray-300 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Enter your new password
          </p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white font-medium" style={{fontFamily: 'Montserrat, sans-serif'}}>
                New Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder-gray-400 focus:bg-white/20 focus:border-white/40"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-400 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  {errors.password}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white font-medium" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Confirm Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder-gray-400 focus:bg-white/20 focus:border-white/40"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-400 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <div className="bg-blue-600/20 border border-blue-600/30 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-200">
                  <p className="font-medium mb-1">Password Requirements:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>At least 8 characters long</li>
                    <li>Use a combination of letters, numbers, and symbols</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black hover:bg-gray-100 font-semibold"
              style={{fontFamily: 'Montserrat, sans-serif'}}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/auth" className="text-white/80 hover:text-white text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
